// @flow

import type {Language, Company, CompaniesSortBy, CompaniesSortOption} from '@/types'

import React, {Component} from 'react'
import {connect} from 'react-redux'
import {contains, map, mergeAll, pluck, findIndex, propEq, isEmpty} from 'ramda'
import {date} from '@/format'
import {appSelectors, portfoliosActions, portfoliosSelectors} from '@/store'

import EditButton from '@/controls/edit-button'
import Flag from '@/elements/flag'
import NoAvailableTableData from '@/elements/no-available-table-data'
import {hideScoreForCountries} from '@/defaults'
import {getReportLinkDetailsForGA, isPermissionEnabled, getColumnsWidth, companiesColumnNames} from '@/views/util'
import {showBandForCountriesWhenLocalRatingIsNotExistForCompany} from '../../defaults'

type Props = {
  companies: Array<Company>,
  portfolioCompaniesSortOptions: CompaniesSortOption,
  saveCompany: (string, number) => void,
  setupEditCompanyForm: (string) => void,
  showCompanyReport: (companyId: string) => void,
  sortBy: (string) => void,
  l: Language,
  langCode: string,
  selectedCompanies: Object,
  updateSelectedCompanies: (Object) => void,
  pageNo: number,
  isLoading: boolean,
  countryCode: string,
  isActivePortfolioShared: boolean,
  activePortfolioPermissions: string,
  activePortfolioId: number,
}

const getMiniCompanyObj = (company) => ({
  safeNumber: company.safeNumber,
  personalReference: company.personalReference,
  freeText: company.freeText,
  personalLimit: company.personalLimit,
})

const updateAll = (props: Props) => {
  const isAll = props.selectedCompanies.isAll // old value
  props.updateSelectedCompanies({'isAll': !isAll, data: []})
}

const checkIsNotAllPortfolio = (companies, isNotAllPortfolio) => companies.length > 0 && isNotAllPortfolio

const update = (currPageCompanies, compObj, selectedCompanies) => (e) => {
  const isChecked = e.target.checked
  let data = [...selectedCompanies.data]
  if (isChecked) {
    // push the company
    data.push(getMiniCompanyObj(compObj))
  } else {
    const index = findIndex(propEq('safeNumber', compObj['safeNumber']))(data)
    if (index > -1) {
      // remove the company
      data.splice(index, 1)
    }
  }
  return {isAll: selectedCompanies.isAll, data}
}

const getClassNameForSortBy = (props: Props) => (column: CompaniesSortBy) => {
  let classNames = ['sort-by']
  const {sortBy, sortDir} = props.portfolioCompaniesSortOptions
  if (sortBy === column) {
    if (sortDir === 'asc') {
      classNames.push('drop-up')
    } else {
      classNames.push('drop-down')
    }
  }
  return classNames.join(' ')
}

const renderRating = ({ratingLocal, ratingCommon, countryCode}, l) => {
  const band = ratingCommon.trim()
  if (isNaN(ratingLocal)) {
    if (!isEmpty(band) && isShowOnlyBandWhenLocalRatingIsNotExist(countryCode)) {
      return <span className={`rating-band badge-${band}`}>{band}</span>
    }
    return <span>{l(ratingLocal)}</span>
  }
  return <span><span className={`rating-band badge-${band}`}>{band}</span>&nbsp;{ratingLocal}</span>
}

const isScoreHidden = (companyCountryCode: string) => {
  return hideScoreForCountries.indexOf(companyCountryCode) > -1
}

const isShowOnlyBandWhenLocalRatingIsNotExist = (companyCountryCode: string) => {
  return showBandForCountriesWhenLocalRatingIsNotExistForCompany.indexOf(companyCountryCode) > -1
}

const getDisplayedPersonalLimit = (personalLimit: string) => {
  return personalLimit ? parseFloat(personalLimit).toFixed(2) : '0.00'
}

const renderHeadersNames = (props, getColumnWidths, column) => <th key={column.key} width={getColumnWidths(column.key)}><a
  onClick={() => props.companies.length > 0 ? props.sortBy(column.key) : null}>{props.l(column.name)}<span
    className={getClassNameForSortBy(props)(column.key)}></span></a></th>

const renderEachRow = ({
  selectedCompanies,
  l,
  saveCompany,
  setupEditCompanyForm,
  showCompanyReport,
  langCode,
  updateSelectedCompanies,
  pageNo,
  companies,
  countryCode,
  activePortfolioPermissions,
  isActivePortfolioShared,
}, getColumnWidths, column) => (company) => {
  if (column.key === 'companyName') {
    return <td key={column.key} width={getColumnWidths('companyName')}>
      <a data-click_through={getReportLinkDetailsForGA(countryCode, company.safeNumber)}
        onClick={() => showCompanyReport(company.safeNumber)}>
        {company.name}
      </a>
    </td>
  }
  if (column.key === 'companyId') {
    return <td key={column.key} width={getColumnWidths(column.key)}>{company['safeNumber']}</td>
  }
  if (column.key === 'countryCode') {
    return <td key={column.key} width={getColumnWidths('countryCode')}><Flag code={company.countryCode} />&nbsp;&nbsp;{company.countryCode}</td>
  }
  if (column.key === 'personalLimit') {
    return <td key={column.key} width={getColumnWidths('personalLimit')}>{getDisplayedPersonalLimit(company.personalLimit)}</td>
  }
  if (column.key === 'localScore') {
    return <td key={column.key} width={getColumnWidths('localScore')}>{!isScoreHidden(company.countryCode) ? renderRating(company, l) : false}</td>
  }
  if (column.key === 'lastEventDate') {
    return <td key={column.key} width={getColumnWidths('lastEventDate')} className='date-format'>{company.dateLastEvent && date(company.dateLastEvent, langCode)}</td>
  }
  return <td key={column.key} width={getColumnWidths(column.key)}>{company[column.key]}</td>
}

const renderRow = (props, isNotAllPortfolio) => (
  company: Company,
) => {
  const isCompanySelected = contains(company.safeNumber, pluck('safeNumber', props.selectedCompanies.data || []))

  const getColumnWidths = getColumnsWidth(isNotAllPortfolio)

  const filteredColumnNames = isNotAllPortfolio
    ? companiesColumnNames.filter((column) => !(isNotAllPortfolio && column.key === 'portfolioName')) : companiesColumnNames
  const companyId = company.portfolioName ? `${company.portfolioName}_${company.safeNumber}` : company.safeNumber
  return <tr key={companyId} className={isCompanySelected ? 'selected' : ''}>
    <td width={isNotAllPortfolio ? '3%' : '2%'}>
      {(!props.isActivePortfolioShared || isPermissionEnabled(props.activePortfolioPermissions, 'Edit'))
        ? <EditButton id='edit_company_details'
          title={'Edit company details'}
          onClick={() => props.setupEditCompanyForm(companyId)}
          onSave={() => props.saveCompany(companyId, company.portfolioId)}
        /> : null}
    </td>
    {checkIsNotAllPortfolio(props.companies, isNotAllPortfolio)
      ? <td width={isNotAllPortfolio ? '3%' : '2%'}>
        <input
          type="checkbox"
          checked={isCompanySelected}
          onChange={(e) => props.updateSelectedCompanies(update(props.companies, company, props.selectedCompanies)(e))}
          disabled={props.selectedCompanies.isAll}
        />
      </td> : null }
    {filteredColumnNames.map((column) => renderEachRow(props, getColumnWidths, column)(company))}
  </tr>
}
const renderRows = (handlers, isNotAllPortfolio) =>
  map(renderRow(handlers, isNotAllPortfolio))

export class RawCompaniesTable extends Component<Props, *> {
  constructor (props: Props) {
    super(props)
    const windowDimensions = {
      windowLength: window.outerHeight,
    }
    this.state = windowDimensions
  }
  updateDimensions = () => {
    this.setState({
      windowLength: window.outerHeight,
    })
  }

  render () {
    const {props} = this
    const isNotAllPortfolio = (props.activePortfolioId !== 0)
    const getColumnWidths = getColumnsWidth(isNotAllPortfolio)
    const filteredColumnNames = isNotAllPortfolio
      ? companiesColumnNames.filter((column) => !(isNotAllPortfolio && column.key === 'portfolioName')) : companiesColumnNames
    return (
      <div className="monitoring-table-ctr monitoring-table-ctr-min-width">
        <table className={props.companies.length > 10 ? 'table table-condensed shrink' : 'table table-condensed'}
          style={{'marginBottom': '0px'}}>
          <thead>
            <tr className={props.companies.length === 0 ? 'disabled-header' : ''}>
              <th className='mark-all' width={getColumnWidths('all')}>
                {checkIsNotAllPortfolio(props.companies, isNotAllPortfolio)
                  ? <div>
                    <span>{props.l('All')}</span>
                    <span>
                      <input id="companies_select_all" type="checkbox" checked={props.selectedCompanies.isAll}
                        onChange={() => updateAll(props)}
                        title={`${props.l('Select this checkbox to copy/move/delete all the companies in the portfolio')}.`}/>
                    </span></div> : null}
              </th>
              {filteredColumnNames.map((column) => renderHeadersNames(props, getColumnWidths, column))}
            </tr>
          </thead>
        </table>
        <div className="tbody-scroll" style={{maxHeight: (this.state.windowLength ? this.state.windowLength - 566 : 162)}}>
          <table className="table table-condensed">
            <tbody>
              {
                props.companies.length
                  ? renderRows(props, isNotAllPortfolio)(props.companies)
                  : <NoAvailableTableData numOfCols={10} text={props.l('No companies found')}/>
              }
            </tbody>
          </table>
        </div>
      </div>
    )
  }
  componentDidMount () {
    window.addEventListener('resize', this.updateDimensions)
  }
}

const mapStateToProps = (state) => ({
  langCode: state.app.user.preferredLanguageCode,
  companies: portfoliosSelectors.getActivePortfolioCompanies(state),
  portfolioCompaniesSortOptions: state.ui.portfolioCompaniesSortOptions,
  l: appSelectors.getL(state),
  isLoading: state.app.showAppLoader,
  countryFilter: state.ui.portfolioCountryFilter,
  searchFilter: state.ui.portfolioSearchFilter,
  countryCode: state.app.user.countryCode,
  isActivePortfolioShared: portfoliosSelectors.isActivePortfolioShared(state),
  activePortfolioPermissions: portfoliosSelectors.getActivePortfolioPermissions(state),
  activePortfolioId: state.app.activePortfolioId,
})

export default connect(
  mapStateToProps,
  mergeAll([portfoliosActions]),
)(RawCompaniesTable)



// WEBPACK FOOTER //
// ./src/views/portfolios-page/companies-table.js